import type {
    Baustein,
    BausteinFormat,
    CurriculumItemReason,
    EmployeeProfile,
    Gap,
    Obligation,
    SkillCategory,
    SkillLevel,
} from "@/modules/lernpfad/engine"
import {
    ASPIRATION_LABELS_EN,
    BAUSTEIN_EN,
    FRAMEWORK_EN,
    OBLIGATION_EN,
    PROVIDER_EN,
    ROLE_LABELS_EN,
    SIGNING_AUTHORITY_EN,
    SKILL_LABELS_EN,
} from "./content-en"
import { CAST_ARTEFACTS_EN } from "./cast-en"
import { createTranslator, type Lang, type Translator } from "./ui"

/**
 * One place that turns engine output into the reader's language.
 *
 * Everything is keyed by ID with the German value as the fallback, so a missing
 * translation degrades to German rather than to a blank — on a demo stage a
 * German word in an English sentence is survivable; an empty heading is not.
 */
export interface Localizer {
    lang: Lang
    t: Translator
    skill: (skillId: string, fallback: string) => string
    role: (roleId: string, fallback: string) => string
    aspiration: (id: string, fallback: string) => string
    baustein: (baustein: Baustein) => { title: string; description: string; provider: string }
    /** A learning framework's prose. Same id-keyed fallback shape as `baustein`. */
    framework: (framework: {
        id: string
        name: string
        claim: string
        changes: string[]
        watch: string
    }) => { name: string; claim: string; changes: string[]; watch: string }
    obligationLabel: (id: string, fallback: string) => string
    obligationBasis: (id: string, fallback: string) => string
    signingAuthority: (value: string | undefined) => string | undefined
    level: (level: SkillLevel) => string
    format: (format: BausteinFormat) => string
    reason: (reason: CurriculumItemReason) => string
    origin: (origin: Gap["origin"]) => string
    /** A skill's subject area — regulatorik/fach/fuehrung/digital/methodik. */
    category: (category: SkillCategory) => string
    artefacts: (employeeId: string, fallback?: EmployeeProfile["artefacts"]) => EmployeeProfile["artefacts"]
    /** Localised date: DD.MM.YYYY for de, D MMM YYYY for en. */
    date: (iso: string) => string
    /** Locale-aware thousands separator. */
    num: (value: number) => string
}

const EN_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

export function createLocalizer(lang: Lang): Localizer {
    const t = createTranslator(lang)
    const isEn = lang === "en"

    return {
        lang,
        t,
        skill: (skillId, fallback) => (isEn ? (SKILL_LABELS_EN[skillId] ?? fallback) : fallback),
        role: (roleId, fallback) => (isEn ? (ROLE_LABELS_EN[roleId] ?? fallback) : fallback),
        aspiration: (id, fallback) => (isEn ? (ASPIRATION_LABELS_EN[id] ?? fallback) : fallback),
        baustein: (baustein) => {
            if (!isEn) {
                return {
                    title: baustein.title,
                    description: baustein.description,
                    provider: baustein.provider,
                }
            }
            const translated = BAUSTEIN_EN[baustein.id]
            return {
                title: translated?.title ?? baustein.title,
                description: translated?.description ?? baustein.description,
                provider: PROVIDER_EN[baustein.provider] ?? baustein.provider,
            }
        },
        framework: (framework) => {
            if (!isEn) {
                return {
                    name: framework.name,
                    claim: framework.claim,
                    changes: framework.changes,
                    watch: framework.watch,
                }
            }
            const translated = FRAMEWORK_EN[framework.id]
            return {
                name: translated?.name ?? framework.name,
                claim: translated?.claim ?? framework.claim,
                changes: translated?.changes ?? framework.changes,
                watch: translated?.watch ?? framework.watch,
            }
        },
        obligationLabel: (id, fallback) => (isEn ? (OBLIGATION_EN[id]?.label ?? fallback) : fallback),
        obligationBasis: (id, fallback) => (isEn ? (OBLIGATION_EN[id]?.basis ?? fallback) : fallback),
        signingAuthority: (value) =>
            value === undefined ? undefined : isEn ? (SIGNING_AUTHORITY_EN[value] ?? value) : value,
        level: (level) => t(`level.${level}` as never),
        format: (format) => t(`format.${format}` as never),
        reason: (reason) => t(`reason.${reason}` as never),
        origin: (origin) => t(`origin.${origin}` as never),
        category: (category) => t(`category.${category}` as never),
        artefacts: (employeeId, fallback) => (isEn ? (CAST_ARTEFACTS_EN[employeeId] ?? fallback) : fallback),
        date: (iso) => {
            const [year, month, day] = iso.split("-")
            if (!isEn) return `${day}.${month}.${year}`
            return `${Number(day)} ${EN_MONTHS[Number(month) - 1]} ${year}`
        },
        // en-GB, not en-CH: the Swiss English locale keeps the apostrophe
        // separator (1'960), which then disagreed with the hand-written English
        // prose on the same screen ("1,960 employees").
        num: (value) => value.toLocaleString(isEn ? "en-GB" : "de-CH"),
    }
}

/** Translate an obligation in place, for the rationale builder. */
export function localizeObligation(obligation: Obligation, localizer: Localizer) {
    return {
        label: localizer.obligationLabel(obligation.id, obligation.label),
        basis: localizer.obligationBasis(obligation.id, obligation.basis),
    }
}
